// Clientul ecranului de localizare: ce s-a citit din acte și ce corectează omul.
//
// Tipurile sunt luate ca atare din UI-ul dezvoltat în aplicația de arhitectură
// — ele descriu exact forma consolidată din `core.project_data`, deci o a doua
// ediție a lor ar fi o a doua definiție a aceluiași lucru.
//
// Separat de `files.ts` fiindcă acela răspunde la „ce PLANȘE are proiectul" —
// categoria de desen, nivelul, corpul, zonele de decupat — iar ăsta la „ce ACTE
// are". Sunt două coloane diferite în aceeași tabelă și două ecrane diferite;
// puse într-un singur client, ar fi imposibil de spus care câmp aparține cui.

// Control plane-ul, nu generatorul: UI-ul unificat nu vorbește niciodată
// direct cu un serviciu intern.
const API = "/api";

import { t } from "../i18n";
import type { Cheie } from "../i18n";

// Cheile sunt cele din `dosar.CATEGORII` (Python). Etichetele NU se scriu aici:
// vin de la server odată cu fișierele, ca o categorie adăugată acolo să apară
// aici fără să fie nevoie de o a doua ediție a aceleiași liste. `ETICHETE` de
// mai jos e doar plasa pentru cazul în care ecranul se desenează înainte să fi
// răspuns serverul.
export type CategorieDosar =
  | "certificat_urbanism"
  | "cadastru"
  | "plan_topografic"
  | "studiu_geotehnic"
  | "solutie_arhitecturala"
  | "solutie_structurala"
  | "retete_preturi"
  | "altele";

const CHEIE_ETICHETA: Record<string, Cheie> = {
  certificat_urbanism: "dosar.categorie.certificat_urbanism",
  cadastru: "dosar.categorie.cadastru",
  plan_topografic: "dosar.categorie.plan_topografic",
  studiu_geotehnic: "dosar.categorie.studiu_geotehnic",
  solutie_arhitecturala: "dosar.categorie.solutie_arhitecturala",
  solutie_structurala: "dosar.categorie.solutie_structurala",
  retete_preturi: "dosar.categorie.retete_preturi",
  altele: "dosar.categorie.altele",
};

// Nu e o piesă de dosar, ci un FEL de fișier: unul cu mai multe acte în el
// (`importer/dosar/clasificare.MIXT`). Nu stă în `CHEIE_ETICHETA`, pe care un
// test o compară cu `dosar.CATEGORII`; cardurile îl arată la fiecare piesă.
const ETICHETA_MIXT: Cheie = "dosar.categorie.mixt";

/** «p. 8–10» — paginile unei piese dintr-un fișier cu mai multe acte. */
export const paginilePiesei = (pagini: [number, number]): string =>
  (pagini[0] === pagini[1] ? t("dosar.piesa.pagina", { p: pagini[0] })
    : t("dosar.piesa.pagini", { de_la: pagini[0], pana_la: pagini[1] }));

/** Numele piesei de dosar, cât timp serverul n-a răspuns încă. */
export const eticheta = (cheie: string): string =>
  (CHEIE_ETICHETA[cheie] ? t(CHEIE_ETICHETA[cheie]) : cheie === "mixt" ? t(ETICHETA_MIXT) : cheie);

/** De unde vine o zonă de acces sau un front: o piesă de dosar, sau harta
 *  OpenStreetMap (`osm`, pusă de `importer/dosar/fronturi_osm.py`) — care nu
 *  e o categorie de dosar, deci nu stă în `CHEIE_ETICHETA`. */
export const sursaZona = (sursa: string): string =>
  (sursa === "osm" ? t("harta.sursa_osm") : eticheta(sursa));

/** Aceeași plasă, ca hartă. Un `Proxy`, nu un obiect gata făcut: construit o
 *  dată la încărcarea modulului, ar fi înghețat limba de atunci, iar
 *  schimbarea ei din profil n-ar mai fi ajuns aici.
 *
 *  Cheile lui sunt cele din `dosar.CATEGORII` (Python), și un test al
 *  importatorului chiar le compară cu ale lui — v. `importer/tests/test_dosar.py`. */
export const ETICHETE: Record<string, string> = new Proxy({}, {
  get: (_, c: string) => eticheta(c),
  has: (_, c: string) => c in CHEIE_ETICHETA,
  ownKeys: () => Object.keys(CHEIE_ETICHETA),
  getOwnPropertyDescriptor: () => ({ enumerable: true, configurable: true }),
});

export interface FisierDosar {
  id: string;
  name: string;
  folder: string;
  size: number | null;
  categorie: string | null;
  dosar: string | null;
  dosar_sursa: "manual" | "regula" | "titlu" | "continut" | "oferte" | null;
  piese?: { dosar: string; pagini: [number, number]; motiv?: string | null }[] | null;
  created_at: string;
}

export interface GrupaDosar {
  cheie: CategorieDosar;
  eticheta: string;
  se_extrage: boolean;
  fisiere: FisierDosar[];
}

export interface Latura {
  de_la: string;
  la: string;
  lungime_m: number;
  tip: "strada" | "vecin";
  catre: string | null;
  // Punctul cardinal spre care dă latura, calculat din conturul cadastral.
  // E ce leagă „la sud: Bulevardul Timișoara" din tabelul de vecinătăți de o
  // latură anume.
  catre_punct?: string | null;
}

/** O ZONĂ DE ACCES: segmentul de pe o latură a conturului pe care poate cădea
 *  accesul carosabil. Nu e un punct — poarta o alege motorul de circulație în
 *  zonă. Aceeași formă, fie că a ieșit din acte (frontul stradal citit de pe
 *  planul topo sau de pe anexa certificatului), fie că a marcat-o omul pe hartă.
 *
 *  Capetele sunt în grade pentru hartă și în Stereo 70 pentru motor; ecranul
 *  scrie gradele, serverul proiectează metrii la salvare (`p*_stereo70` sunt
 *  deci CITITE, nu scrise de ecran). `latura`, `de_la_m` și `pana_la_m` sunt
 *  ale conturului de la momentul derivării — orientative; ce contează sunt
 *  capetele, pe care fiecare consumator le lipește el pe conturul curent.
 *
 *  `exact` = e chiar gura de acces existentă, citită de pe topo. */
export interface ZonaAcces {
  id?: string;
  latura?: number;
  de_la_m?: number;
  pana_la_m?: number;
  p0_wgs84: [number, number];
  p1_wgs84: [number, number];
  p0_stereo70?: [number, number] | null;
  p1_stereo70?: [number, number] | null;
  catre?: string | null;
  exact?: boolean;
  sursa: "plan_topografic" | "cadastru" | "solutie_arhitecturala"
       | "certificat_urbanism" | "manual" | string;
}

/** Un front stradal: o latură a conturului care dă spre o arteră, cu numele
 *  ei și din ce act a ieșit. */
export interface Front {
  latura: number;
  catre: string | null;
  sursa: string;
  cum: "varfuri" | "schita" | "busola" | "laturi" | string;
}

/** O bandă a profilului transversal al străzii, de la limita lotului spre
 *  partea opusă. Același vocabular ca `importer/dosar/profil_strada.FELURI`
 *  și ca desenul din `exporter/pud/profil.py`. */
export type FelBanda = "trotuar" | "verde" | "carosabil" | "parcare_paralela"
  | "parcare_perpendiculara" | "pista_biciclete" | "tramvai";
export const FELURI_BANDA: FelBanda[] = ["trotuar", "verde", "carosabil", "parcare_paralela",
                                         "parcare_perpendiculara", "pista_biciclete", "tramvai"];

/** PROFILUL EXISTENT al străzii de pe un front, citit de model din
 *  ortofotografie și din vederile din stradă (`sursa: ortofoto_model`) sau
 *  corectat de om (`manual`). E o aproximare: siguranța și observațiile
 *  modelului se arată, iar omul poate rescrie benzile. */
export interface ProfilStrada {
  catre: string | null;
  /** sensul e al fiecărei benzi de carosabil, nu al străzii: un bulevard cu
   *  două carosabile cu sens unic nu e «dublu sens» */
  benzi: { fel: FelBanda | string; latime_m: number; sens?: "unic" | "dublu" | null }[];
  latime_totala_m: number | null;
  capat_opus: string | null;
  siguranta: number | null;
  observatii: string | null;
  sursa: "ortofoto_model" | "manual" | string;
  adus_la?: string | null;
}

export interface Parcela {
  numar_cadastral: string | null;
  carte_funciara: string | null;
  suprafata_acte_mp: number | null;
  suprafata_masurata_mp: number | null;
  constructii: string | null;
}

/** O BUCATĂ DE LOT CU CONTURUL EI, dintr-un cadastru propriu.
 *
 *  Un teren mare se cumpără pe bucăți: pe Șos. Fundeni, 233A + 233C + 233D,
 *  trei numere cadastrale și trei inventare de coordonate. Importul le adună
 *  într-un singur lot (`contur_wgs84` e reuniunea lor), dar le ține și
 *  separat — ele sunt ce scrie în acte și ce se vede pe hartă ca parcele.
 *  V. `dosar/citire::_parcelele_lotului`. */
import type { FormulaRetragere, ZonaRetragere } from "./edificabil";

export type { FormulaRetragere, ZonaRetragere };

export interface ParcelaContur {
  numar_cadastral: string | null;
  carte_funciara?: string | null;
  adresa: string | null;
  sursa: string;
  suprafata_mp: number;
  /** Suprafețele SCRISE în actele bucății. `suprafata_mp` de deasupra e
   *  măsurată pe inel. */
  suprafata_acte_mp?: number | null;
  suprafata_masurata_mp?: number | null;
  /** REGIMUL BUCĂȚII, din certificatul care o privește — nu al lotului.
   *
   *  Cerut pe 14.10.2026: «if we have separate certificate de urbanism, make
   *  sure that the information from all certificates and files are joined and
   *  we hold all info about the lot as a whole (including all pieces)».
   *  Bucățile unui lot pot cădea în UTR-uri diferite, cu alt POT și alte
   *  retrageri; până acum rămânea doar certificatul primului fișier în ordine
   *  alfabetică, iar restul se pierdeau. Gol pe un lot dintr-o singură
   *  parcelă: acolo regimul lotului E al parcelei.
   *  V. `dosar/citire::_regimul_parcelelor`. */
  regim_urbanistic?: Partial<RegimUrbanistic> | null;
  /** Din ce fișier vine fiecare cifră A BUCĂȚII. */
  provenienta?: Record<string, string> | null;
  /** Toate actele care o descriu: inventarul, extrasul, certificatul. */
  acte?: string[] | null;
  contur: [number, number][];
  contur_wgs84: [number, number][] | null;
}

/** UN DRUM, păstrat lângă lot fără să intre în el.
 *
 *  Cerut pe 14.10.2026: «the importer should check if a piece of land is a
 *  road and if it is it should be highlighted different and it should have the
 *  appropriate information on the right panel», și, de la șeful Biancăi,
 *  «trebuie să păstrăm drumul -> să îl desenăm în ecranul de plot details».
 *
 *  Suprafața lui NU e a lotului: POT-ul și CUT-ul se calculează din suprafața
 *  lotului, iar un drum adunat la teren «strică baza de calcul a POT cu un
 *  sfert». Dar nici nu se aruncă — de el se măsoară retragerea de la
 *  aliniament. V. `dosar/citire::_desparte_drumurile`. */
export interface Drum {
  numar_cadastral: string | null;
  /** Numele arterei, cum îl scrie actul. */
  nume: string | null;
  categoria_folosinta: string | null;
  /** «cale_acces» — drumul ACESTUI lot, cel pe care se intră în el, dat ca
   *  atare de certificat sau de mențiunea planului — ori «drum», un vecin care
   *  nu se construiește. Pe prima, ecranul scrie «Cale de acces». */
  fel?: "cale_acces" | "drum" | null;
  /** Mențiunea din planul de amplasament — «Lot 1 - Cale de acces». Acolo scrie
   *  la ce servește parcela; categoria de folosință a unei căi de acces e tot
   *  «Cc», ca a oricărei curți. */
  mentiuni?: string | null;
  sursa: string;
  /** De unde se știe că e drum, scris pentru om. */
  de_ce: string | null;
  suprafata_mp: number | null;
  lungime_m: number | null;
  latime_m: number | null;
  /** Laturile lotului LIPITE de drum. Ele sunt front stradal — o probă
   *  geometrică, nu o deducție din text sau busolă —, deci pe ele se aplică
   *  retragerea de la aliniament, nu cea de la vecin. */
  laturi?: number[] | null;
  /** Cât de departe e drumul de lot. 0 = lipit; mai mult înseamnă că între ele
   *  e teren al altcuiva, deci drumul nu dă front. */
  distanta_la_lot_m?: number | null;
  contur: [number, number][];
  contur_wgs84: [number, number][] | null;
}

export interface RegimUrbanistic {
  pot_max: number | null;
  cut_max: number | null;
  regim_inaltime: string | null;
  niveluri_supraterane: number | null;
  niveluri_subsol: number | null;
  inaltime_max_m: number | null;
  inaltime_nivel_m: number | null;
  retragere_strada_m: number | null;
  retragere_vecini_m: number | null;
  // RETRAGEREA CA BANDĂ PE ÎNĂLȚIME, fiindcă așa o scriu actele: «Hmax ≤ 20,0 m
  // → ≥ 5,0 m; > 20,0 și ≤ 30,0 → ≥ 7,5 m; > 30,0 → ≥ 10,0 m». Strivită într-un
  // singur număr, s-ar pierde de două ori — la citire, unde cineva alege una din
  // trei fără să știe ce se va construi, și la schimbarea regimului din ecran,
  // unde ar rămâne a înălțimii vechi. `h_max_m: null` e banda de deasupra tuturor.
  retragere_vecini_benzi?: { h_max_m: number | null; setback_m: number }[] | null;
  /** ȘI CA FORMULĂ: «H/3 dar nu mai puțin de 6,0 m» — aceeași specie de regulă
   *  ca o bandă, scrisă continuu. Se păstrează ca regulă, nu ca metraj: un
   *  număr calculat o dată la import se învechește tăcut când omul schimbă
   *  regimul de înălțime. V. `edificabil.retragereDinFormula`. */
  retragere_vecini_formula?: FormulaRetragere | null;
  retragere_strada_formula?: FormulaRetragere | null;
  /** Distanța dintre corpurile de pe aceeași parcelă, ca regulă pe înălțime:
   *  «H/3 dar nu mai puțin de 6,0 m». Lipsea din import cu totul, deci rândul
   *  «Între clădiri» cădea întotdeauna pe H/2 al nostru. */
  retragere_intre_cladiri_formula?: FormulaRetragere | null;
  /** RETRAGEREA DE LA ALINIAMENT, pe categoria străzii: «3 m pentru cat. IV și
   *  III, 5 m pentru cat. II și I». Categoria nu scrie în certificat, deci nu
   *  se poate alege un rând — dar asta NU înseamnă că actul n-a spus nimic.
   *  Cât timp categoria e necunoscută se folosește cel mai strict rând
   *  (v. `edificabil.retragereDeLaAliniament`), nu H/2. */
  retragere_aliniament_categorii?: { categorii: string | null;
                                     retragere_m: number }[] | null;
  // Distanța dintre corpurile de pe aceeași parcelă, când actul o dă. Fără ea,
  // regula e jumătate din înălțime. Nu e o retragere față de o limită, deci nu
  // se poate deduce din celelalte două: un PUD poate cere 20 m între blocuri și
  // 5 m până la vecin.
  retragere_intre_cladiri_m: number | null;
  spatiu_verde_min: number | null;
  // Fracția din SPAȚIUL VERDE care trebuie să stea pe sol natural (neescavat),
  // nu fracția din parcelă: „spații verzi minim 30%, din care cel puțin 2/3 la
  // sol" e `spatiu_verde_min: 0.30` și `spatiu_verde_la_sol_min: 0.667`.
  // Ecranul arată și m², fiindcă două procente unul sub altul se citesc altfel.
  spatiu_verde_la_sol_min: number | null;
  utr: string | null;
  functiune_zona: string | null;
  locuri_parcare_norma: string | null;
  /** TERENUL ÎN MAI MULTE ZONE DE REGIM (UTR Lc + UTR M1 pe același lot, ca
   *  la Pipera): fiecare cu indicatorii ei, cu aceleași chei ca aici. Câmpurile
   *  de deasupra sunt atunci copia ZONEI ACTIVE (`zona_activa`), ca tot ce
   *  consumă un singur regim să meargă neschimbat. Lipsește pe un lot cu un
   *  singur UTR. V. `importer/dosar/zone_urbanistice.py`. */
  zone?: ZonaUrbanistica[] | null;
  zona_activa?: string | null;
}

/** O zonă de regim a terenului: numele din act («UTR M1»), funcțiunea,
 *  indicatorii ei (aceleași chei ca `RegimUrbanistic`) și, când se știe, unde
 *  cade pe teren. */
export interface ZonaUrbanistica extends Partial<Omit<RegimUrbanistic, "zone" | "zona_activa">> {
  nume: string;
  aliasuri?: string[] | null;
  functiune?: string | null;
  /** Suprafața SCRISĂ în acte (analiza PUZ) și cea MĂSURATĂ pe conturul zonei. */
  suprafata_acte_mp?: number | null;
  suprafata_mp?: number | null;
  pozitie_text?: string | null;
  contur?: [number, number][] | null;
  contur_wgs84?: [number, number][] | null;
  /** De unde vine conturul: fâșia sau suprafața din act, restul lotului,
   *  mâna omului, planșa citită cu modelul; `null` = nu se știe. */
  geometrie_din?: "act_fasie" | "act_suprafata" | "rest" | "manual" | "plansa_astra" | null;
  reteta?: { fel: string; adancime_m?: number; arie_mp?: number } | null;
}

export interface TipApartament {
  tip: string;
  numar: number | null;
  procent: number | null;
  suprafata_utila_mp: number | null;
}

/** Nivelul de calitate, global și pe capitole.
 *
 *  `nivel` e alegerea care se ia o dată și ține tot proiectul. Capitolele sunt
 *  `null` cât timp îl urmează — nu o copie a lui: copiate, o schimbare a
 *  nivelului global nu le-ar mai mișca, iar omul care a mutat o dată cursorul
 *  pe „premium" ar rămâne cu șase decizii vechi pe care nu și le amintește.
 *  Scrise, ele spun „aici am vrut altceva decât peste tot".
 */
export interface Calitate {
  nivel: string | null;
  compartimentare: string | null;
  finisaje: string | null;
  fatada: string | null;
  hvac: string | null;
  sanitare: string | null;
  electrice: string | null;
}

export interface Tema {
  /** Ce vrea omul, în cuvintele lui. Câmpurile de dedesubt sunt aceeași
   *  dorință tăiată în bucăți pe care le poate mesteca un motor; textul ăsta e
   *  singurul loc în care încape „vreau parterul deschis spre curte". */
  descriere: string | null;
  tip_dezvoltare: string | null;
  regim_inaltime: string | null;
  structura_apartamentelor: TipApartament[];
  numar_apartamente: number | null;
  tip_parcare: string | null;
  locuri_parcare: number | null;
  finisaj: string | null;
  calitate: Calitate | null;
  /** Fațada, din pasul 4. Se ține în temă; o citește agentul de fațadă după
   *  etajele fiecărei arhitecturi — v. `AlegereFatada`. */
  fatada: AlegereFatada | null;
  /** VOLUMETRIA, două bife (Cătălin, 1.10.2026). `terase_retrase`: ultimul
   *  etaj se retrage la capetele libere și fâșia devine terasă (regula din
   *  `architecture/layout/terasa.py`); planul de situație scade terasele din
   *  ADC ca CUT-ul realizat să fie plafonul. `inaltimi_diferite`: corpurile
   *  pot avea segmente cu niveluri diferite (de la regim până la regim − 3,
   *  pe colțuri, capete și rosturi). `null` = implicitul: terase da, trepte nu. */
  terase_retrase: boolean | null;
  inaltimi_diferite: boolean | null;
  /** BIFA C (Cătălin, 8.10.2026): corpuri cu înălțimi diferite între ele, de
   *  la `regim_minim` («P+8») până la regimul ales. Independentă de celelalte
   *  două: regimul corpului se alege întâi, treptele coboară de la el, terasa
   *  stă pe sus-ul lui. `null` = stinsă, toate corpurile la regimul ales. */
  corpuri_diferite: boolean | null;
  regim_minim: string | null;
  /** SPAȚIILE DE SERVICII LA PARTER (Cătălin, 27.10.2026): bifa și procentul
   *  (0–100) din suprafața construită admisibilă. Planul de situație alege
   *  corpurile al căror parter întreg, însumat, se apropie cel mai mult de el;
   *  acele partere au 5 m, pardoseala la teren și fațadă cortină. `null` =
   *  fără spații comerciale, planul de azi. */
  spatii_comerciale: boolean | null;
  procent_comercial: number | null;
  /** CORPURILE DE FUNCȚIUNE (Cătălin, 30.10.2026): școala, clădirea de birouri
   *  și comercialul de sine stătător, fiecare cu bifa ei și metrii pătrați
   *  construiți țintiți. Planul de situație le așază ca volume fără module,
   *  cu niveluri de 5 m, cât mai înalte, înaintea blocurilor. `null` = niciuna. */
  functiuni: FunctiuniTema | null;
  /** SCHIȚA DE VOLUMETRIE (Cătălin, 8.10.2026): volumele desenate de om pe
   *  teren în pasul «Volumetrie», ca GHIDAJ al planului de situație — motorul
   *  ia din ele orientările, numărul de clădiri, formele și pozițiile, dar
   *  optimizează pe CUT. `null` sau fără volume = fluxul de azi, neatins.
   *  V. `volumetrie/schita.ts` și `positioning_plan/docs/SCHITA-VOLUMETRIE.md`. */
  schita: SchitaVolumetrie | null;
  /** Ce a pus în temă PIAȚA DIN ZONĂ, singură (`pages/PiataPeBrief.tsx`): mixul
   *  și standardul scrise, cu colectarea din care vin. Cât tema le are încă
   *  neschimbate, o căutare nouă le înlocuiește; schimbate de om, rămân ale lui.
   *  `null` = piața n-a scris nimic. */
  piata?: UrmaPietei | null;
}

/** Urma pieței în temă: ce a scris ultima dată, ca să se poată spune dacă tema
 *  e încă a pieței (aceleași valori) sau a omului (altele). */
export interface UrmaPietei {
  colectare: number;
  structura: TipApartament[];
  nivel: string | null;
}

/** Formele pe care motorul le poate compune din module. */
export type FormaVolum = "bara" | "L" | "U" | "C";

/** Un volum desenat, cu PARAMETRII lui (pentru editare) și conturul calculat
 *  din ei (`poly`, singurul lucru pe care îl citește motorul). Toate în reperul
 *  lotului — aceleași perechi `[N, E]` în metri ca `contur_stereo70`. */
export interface VolumSchita {
  id: string;
  forma: FormaVolum;
  /** centrul brațului principal */
  centru: [number, number];
  /** orientarea brațului principal, grade, în reperul lotului */
  unghi_deg: number;
  latime_m: number;
  /** lungimile brațelor: bară `[L]`; L `[L, aripă]`; U/C `[L, aripă1, aripă2]` */
  brate_m: number[];
  /** la L: capătul la care stă aripa (−1 / +1); la U și C amândouă capetele */
  capat: -1 | 1;
  /** pe ce parte a brațului principal ies aripile (−1 / +1); la C a doua aripă
   *  iese pe partea cealaltă */
  parte: -1 | 1;
  poly: [number, number][];
}

export interface SchitaVolumetrie {
  versiune: 1;
  volume: VolumSchita[];
  sursa: "om" | "sugestie";
}

/** Sistemul de fațadă: CU CE se face, deci costul și grosimea — nu stilul. */
export type SistemFatada = "etics" | "etics_placari" | "ventilata";

/** O fațadă care îi place omului: una din catalog sau o poză a lui.
 *
 *  Poza încărcată se urcă O SINGURĂ DATĂ în `core.poza_fatada` (micșorată în
 *  browser), iar în temă rămâne doar id-ul ei (`poza`). Înainte stătea în temă
 *  ca data URL de ~100 KB, iar tema se trimite întreagă la fiecare corecție:
 *  un clic pe «Standardul clădirii» urca imaginea din nou, 3–4 s prin tunel
 *  (Cătălin, 7.10.2026). Nu în dosar: fișierele dosarului trec prin import și
 *  clasificare, iar o fațadă văzută pe stradă nu e un act al proiectului.
 *  `imagine` (data URL) rămâne citit numai pentru temele de dinainte. */
export type ReferintaFatada =
  | { sursa: "catalog"; id: string; stil: string }
  | { sursa: "incarcata"; nume: string; poza?: string; imagine?: string };

/** Urcă poza (data URL) și întoarce id-ul ei. Aceeași poză urcată din nou e
 *  același id. */
export async function urcaPozaFatada(pid: string, nume: string, imagine: string): Promise<string> {
  const r = await fetch(`${API}/projects/${pid}/fatada/poze`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ nume, imagine }),
  }).then(json<{ id: string }>);
  return r.id;
}

/** ESTIMAREA SCHIȚEI DE VOLUMETRIE (8.10.2026): POT-ul și CUT-ul pe care le-ar
 *  da volumele desenate, cu regulile motorului — marcate «estimat» pe ecran —,
 *  zona edificabilă în care trebuie să stea, distanța cerută între corpuri și
 *  ce n-ar trece. Citire, fără job. */
export interface EstimareSchita {
  teren_m2: number;
  niveluri: number;
  cut_max: number;
  pot_max: number;
  gap_m: number;
  banda_m: number;
  fata_finita_m: number;
  /** inelele zonei edificabile, [N, E] */
  zona: [number, number][][];
  zona_zidarie: [number, number][][];
  amprenta_m2: number;
  adc_m2: number;
  pot: number;
  cut: number;
  volume: { id: string; forma: string; amprenta_m2: number; adc_m2: number;
            deduceri_m2: number; in_zona: boolean }[];
  perechi_sub_distanta: [string, string, number][];
  note: string[];
}

export async function estimeazaSchita(pid: string, schita: SchitaVolumetrie): Promise<EstimareSchita> {
  return fetch(`${API}/projects/${pid}/schita/estimeaza`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ schita }),
  }).then(json<EstimareSchita>);
}

/** O așezare propusă de motor pentru pânza de volumetrie: volumele ei, cu
 *  parametrii din care se pot redesena. */
export interface VolumSugerat {
  id: string;
  forma: string;
  centru: [number, number];
  unghi_deg: number;
  latime_m: number;
  brate_m: number[];
  capat: number;
  parte: number;
  poly: [number, number][];
}

export interface SugestieVolumetrie {
  volume: VolumSugerat[];
  amprenta_m2: number;
  forme: string[];
  unghi_deg: number;
}

export async function sugestiiVolumetrie(pid: string, n = 6):
    Promise<{ sugestii: SugestieVolumetrie[]; secunde: number; motiv?: string }> {
  return fetch(`${API}/projects/${pid}/schita/sugestii`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ n }),
  }).then(json<{ sugestii: SugestieVolumetrie[]; secunde: number; motiv?: string }>);
}

/** De unde se arată o poză de fațadă a omului: după id, sau data URL-ul unei
 *  teme vechi. */
export function srcPozaFatada(pid: string, r: { poza?: string | null; imagine?: string | null }):
    string | null {
  if (r.poza) return `${API}/projects/${pid}/fatada/poze/${r.poza}`;
  return r.imagine || null;
}

/** Ce s-a ales în pasul Fațada.
 *
 *  `sistem` e `null` cât timp urmează standardul clădirii (ca un capitol din
 *  `Calitate`). `referinte` sunt între zero și trei, în ordinea alegerii:
 *  fiecare poate da o variantă, iar zero înseamnă «alegeți voi». */
export interface AlegereFatada {
  sistem: SistemFatada | null;
  referinte: ReferintaFatada[];
}

/** Tema unui proiect care n-a fost atins de nimeni. Constantă exportată, nu
 *  literal copiat în fiecare ecran: cele două ecrane care o citesc ar fi
 *  divergit la primul câmp nou. */
export const TEMA_GOALA: Tema = {
  descriere: null, tip_dezvoltare: null, regim_inaltime: null,
  structura_apartamentelor: [], numar_apartamente: null, tip_parcare: null,
  locuri_parcare: null, finisaj: null, calitate: null, fatada: null,
  terase_retrase: null, inaltimi_diferite: null,
  corpuri_diferite: null, regim_minim: null,
  spatii_comerciale: null, procent_comercial: null, functiuni: null, schita: null, piata: null,
};

/** Funcțiunile nerezidențiale ale temei, în ordinea de pe ecran. */
export const FUNCTIUNI_TEMA = ["scoala", "birouri", "comercial"] as const;
export type FunctiuneTema = (typeof FUNCTIUNI_TEMA)[number];
export interface CerereFunctiune {
  activ: boolean;
  /** metri pătrați construiți (ADC) țintiți; `null` = bifată fără cifră */
  adc_m2: number | null;
}
export type FunctiuniTema = Partial<Record<FunctiuneTema, CerereFunctiune>>;

/** Funcțiunile BIFATE FĂRĂ m² țintiți, în ordinea de pe ecran. O asemenea bifă
 *  nu construiește nimic — planul o lasă deoparte cu o presupunere pe care omul
 *  n-o vede decât în raport — așa că tema nu-l lasă să treacă mai departe până
 *  nu scrie cifra (Cătălin, 1.11.2026). Goală = se poate continua. */
export function functiuniFaraM2(functiuni: FunctiuniTema | null | undefined): FunctiuneTema[] {
  return FUNCTIUNI_TEMA.filter((f) => {
    const c = functiuni?.[f];
    return !!c?.activ && !(typeof c.adc_m2 === "number" && c.adc_m2 > 0);
  });
}

export interface Conflict {
  camp: string;
  valori: { valoare: unknown; sursa: string }[];
  folosit: string;
}

export interface Localizare {
  adresa: string | null;
  adresa_parti: Record<string, string> | null;
  lat: number | null;
  lon: number | null;
  // Punctul e centrul parcelei măsurate (false) sau strada geocodată din
  // adresă (true). Ecranul o spune, harta o desenează altfel.
  pozitie_aproximativa: boolean;
  // Conturul citit de model n-a trecut verificarea cu actul (aria măsurată,
  // forma): rămâne desenat, dar e spus, cu vârfurile de verificat.
  contur_nesigur?: {
    sursa: string;
    arie_contur_m2: number | null;
    arie_act_m2: number | null;
    abatere: number | null;
    autointersectie: boolean;
    puncte_suspecte: string[];
  }[] | null;
  numar_cadastral: string | null;
  carte_funciara: string | null;
  beneficiar: string | null;
  suprafata_teren_mp: number | null;
  suprafata_acte_mp: number | null;
  suprafata_masurata_mp: number | null;
  // Alipire de mai multe parcele? Atunci `suprafata_teren_mp` e totalul, iar
  // `parcele` spune din ce e făcut — fără asta, o parcelă măsurată separat
  // arată ca o contradicție.
  operatiune: string | null;
  parcele: Parcela[];
  // Ce condiționează construirea: P.U.D. obligatoriu, zone de protecție,
  // rețele edilitare în subteran. Citite din certificat.
  constrangeri: string[];
  necesita_pud: boolean | null;
  contur_wgs84: [number, number][] | null;
  contur_stereo70: [number, number][] | null;
  /** Bucățile lotului, când el vine din mai multe cadastre. Conturul de
   *  deasupra e reuniunea lor. */
  parcele_contur: ParcelaContur[] | null;
  /** DRUMURILE din acte, păstrate lângă lot fără să intre în suprafața lui. */
  drumuri: Drum[] | null;
  /** PORȚIUNILE CU RETRAGERE PROPRIE, așezate pe contur: «între punctele 9-10
   *  retragerea e 6,00 m». V. `edificabil.conturTaiatPeZone`. */
  retrageri_zone: ZonaRetragere[] | null;
  /** RETRAGERILE TRASE DE MÂNĂ, câte una per latură a conturului SALVAT —
   *  `null` unde omul n-a atins nimic.
   *
   *  Indexate pe conturul salvat, nu pe cel tăiat pe porțiuni: tăierea se
   *  schimbă la fiecare salvare, deci indicii s-ar muta sub valori și a doua
   *  tragere ar nimeri altă latură (găsit pe 15.10.2026). */
  retrageri_manual: (number | null)[] | null;
  // Fronturile stradale și zonele de acces carosabil. Fără cel puțin o zonă,
  // generarea nu poate porni: ghidul cere o intrare auto, iar înainte motorul
  // o punea singur pe latura pe care adaptorul o PRESUPUNEA a fi strada. Zonele
  // ies din acte; când nu ies, `provenienta_cat.zone_acces` e «lipsa» și omul
  // le marchează pe hartă.
  fronturi: Front[] | null;
  /** Profilul existent al străzii pe fiecare front, cu cheia = indicele
   *  laturii ca șir (`"0"`), cum îl scrie importerul în `project_data`. */
  profile_strada?: Record<string, ProfilStrada> | null;
  zone_acces: ZonaAcces[] | null;
  laturi: Latura[];
  regim_urbanistic: RegimUrbanistic;
  tema: Tema;
  // Din ce fișier a venit fiecare valoare. Ecranul o arată la hover: o valoare
  // fără proveniență e o valoare în care nimeni nu are motiv să aibă încredere.
  provenienta: Record<string, string>;
  // Din ce FEL de act — cheia de categorie, nu numele fișierului. Chip-ul de
  // lângă etichetă are loc doar pentru „din CU"; numele întreg stă în tooltip.
  provenienta_cat: Record<string, string>;
  conflicte: Conflict[];
  citite: Record<string, string[]>;
  // Câmpurile pe care le-a schimbat omul, ca să se poată distinge de cele
  // citite din acte.
  manual: string[];
  updated_at: string;
  /** ZONA DE ACCES A CAMIOANELOR (București A/B): o socotește serverul din
   *  coordonate la fiecare citire, sau o ia pe cea aleasă de om (`zona_acces`
   *  în corecții). Lipsește la serverele mai vechi. */
  acces?: ZonaAccesCamioane;
}

/** Zona TAXEI DE ACCES a camioanelor — nu zona de acces carosabil de pe
 *  conturul lotului (`ZonaAcces`). */
export interface ZonaAccesCamioane {
  oras: string | null;
  zona: "A" | "B" | null;
  /** «coordonate» = din punctul lotului, «om» = aleasă pe ecran, null = nu se știe */
  sursa: "coordonate" | "om" | null;
  cunoscut: boolean;
  /** la mai puțin de ~250 m de un inel: zona merită confirmată */
  la_limita: boolean;
  distanta_limita_m: number | null;
  din_coordonate: "A" | "B" | null;
  exactitate?: "contur" | "punct" | "strada" | "localitate";
}

export interface ConfigHarta {
  provider: "osm" | "google";
  google_key: string | null;
  tile_url: string;
  attribution: string;
  max_zoom: number;
  /** Fundalul e licențiat DOAR ca fundal (Google). Sub uneltele de desen se
   *  pun alte dale — vezi `Harta.tsx`. */
  doar_fundal?: boolean;
}

async function json<T>(r: Response): Promise<T> {
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
  return r.json() as Promise<T>;
}

/** Forma întoarsă de control plane: datele, proveniența și ce a atins omul. */
interface DateProiect {
  data: Record<string, unknown>;
  provenance: Record<string, string>;
  manual: string[];
  updated_at: string | null;
  acces?: ZonaAccesCamioane;
}

export async function citesteLocalizare(pid: string): Promise<Localizare | null> {
  const d = await fetch(`${API}/projects/${pid}/data`).then(json<DateProiect>);
  if (!d.updated_at && Object.keys(d.data).length === 0) return null;
  // Ecranul lucrează cu un singur obiect plat, ca înainte: consolidatul cu
  // corecturile deja suprapuse, plus lista câmpurilor atinse de om.
  return { ...(d.data as unknown as Localizare), manual: d.manual,
           provenienta: d.provenance, updated_at: d.updated_at ?? "", acces: d.acces };
}

// Corecțiile omului. Se trimite DOAR câmpul atins — serverul le contopește
// peste ce era, iar un `null` șterge corecția și readuce valoarea din act.
export async function salveazaLocalizare(
  pid: string, corectii: Record<string, unknown>,
): Promise<Localizare | null> {
  await fetch(`${API}/projects/${pid}/data`, {
    method: "PATCH", headers: { "Content-Type": "application/json" },
    body: JSON.stringify(corectii),
  }).then(json<unknown>);
  return citesteLocalizare(pid);
}

export interface Candidat { lat: number; lon: number; nume: string; tip: string | null }

// Caută o adresă. Pentru dosarele fără niciun act — scrii unde e lotul, alegi
// din listă, apoi îl desenezi peste hartă.
export function cautaAdresa(q: string): Promise<{ rezultate: Candidat[]; eroare?: string }> {
  return fetch(`${API}/geo/search-address?q=${encodeURIComponent(q)}`)
    .then(json<{ rezultate: Candidat[]; eroare?: string }>);
}

export function configHarta(): Promise<ConfigHarta> {
  return fetch(`${API}/geo/map`).then(json<ConfigHarta>);
}
